import React from 'react';
import { C, CARD_W, CARD_H, nx, ny } from '../flowCanvas.constants';

export default function CardNode({ step, selected, onClick, centreY, overrideX, overrideY }) {
  const x    = overrideX ?? nx(step), y = overrideY ?? ny(step, centreY);
  const left = x - CARD_W / 2;
  const top  = y - CARD_H / 2;

  const data       = step.populatedData;
  const pct        = data?.completionPercentage ?? 0;
  const complete   = data?.isComplete === true;
  const incomplete = data?.isComplete === false;
  const accent     = complete ? C.purple : incomplete ? C.orange : C.purple;

  const groupCount    = data?.groups?.length ?? 0;
  const groupComplete = data?.groups?.filter(g => g.isComplete).length ?? 0;
  const varCount      = data?.variables?.length ?? 0;
  const varComplete   = data?.variables?.filter(v => v.hasValue).length ?? 0;
  const countText = groupCount > 0
    ? `${groupComplete} / ${groupCount}`
    : varCount > 0 ? `${varComplete} / ${varCount}` : null;

  const BAR_X = left + 12;
  const BAR_Y = top + CARD_H - 13;
  const BAR_W = CARD_W - 24;

  const title = step.stepTitle || '';
  const desc  = step.stepDescription || '';
  const summaryLines = (data?.variables || [])
    .slice(0, 3)
    .map(variable => {
      const label = variable?.displayLabel || variable?.variableName || '';
      const value = variable?.hasValue ? String(variable.displayValue ?? '') : 'Not set';
      return `${label}: ${value}`;
    })
    .filter(Boolean);
  const showSummaryLines = step?.stepType === 'customisation' && summaryLines.length > 0;

  return (
    <g className="fc-node fc-node--card" onClick={onClick} style={{ cursor: 'pointer' }}>
      {/* shadow */}
      <rect x={left + 2} y={top + 4} width={CARD_W} height={CARD_H} rx="8" fill="#000" fillOpacity="0.3" />
      {/* card */}
      <rect x={left} y={top} width={CARD_W} height={CARD_H} rx="8"
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : accent}
        strokeWidth={selected ? 2 : 1.5}
      />
      {/* top accent bar */}
      <rect x={left + 10} y={top} width={CARD_W - 20} height="3" rx="1.5" fill={accent} />

      {/* title */}
      <text x={left + 12} y={top + 22} fontSize="11.5" fontWeight="700" fill={C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {title.length > 20 ? title.slice(0, 20) + '…' : title}
      </text>

      {/* description */}
      {desc && !showSummaryLines && (
        <text x={left + 12} y={top + 38} fontSize="9" fill={C.textMuted}
          style={{ pointerEvents: 'none', userSelect: 'none' }}>
          {desc.length > 28 ? desc.slice(0, 28) + '…' : desc}
        </text>
      )}

      {showSummaryLines && summaryLines.map((line, index) => (
        <text
          key={`${step.stepId}-summary-${index}`}
          x={left + 12}
          y={top + 38 + index * 12}
          fontSize="8.5"
          fill={index === 0 ? C.textMuted : C.textDim}
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {line.length > 31 ? line.slice(0, 31) + '…' : line}
        </text>
      ))}

      {/* count */}
      {countText && (
        <text x={left + 12} y={showSummaryLines ? top + 76 : (desc ? top + 55 : top + 45)} fontSize="9" fill={C.textDim}
          style={{ pointerEvents: 'none', userSelect: 'none' }}>
          {countText}
        </text>
      )}

      {/* progress bar */}
      {data && (
        <>
          <rect x={BAR_X} y={BAR_Y} width={BAR_W} height="4" rx="2" fill={C.progress} />
          <rect x={BAR_X} y={BAR_Y} width={Math.max(0, BAR_W * (pct / 100))} height="4" rx="2" fill={accent} />
        </>
      )}

      {complete && (
        <>
          <circle cx={left + CARD_W - 12} cy={top + 12} r="8" fill={C.purple} />
          <text x={left + CARD_W - 12} y={top + 16} textAnchor="middle"
            fontSize="8" fill="#000" style={{ pointerEvents: 'none', userSelect: 'none' }}>✓</text>
        </>
      )}
      {incomplete && (
        <>
          <circle cx={left + CARD_W - 12} cy={top + 12} r="8" fill={C.orange} />
          <text x={left + CARD_W - 12} y={top + 16} textAnchor="middle"
            fontSize="8" fill="#fff" style={{ pointerEvents: 'none', userSelect: 'none' }}>!</text>
        </>
      )}
    </g>
  );
}
